// Comparison — a metric-vs-metric scatter of the whole real player population, ported
// from js/renderers-comparison.js via the 04b bridge.
//   04i-b (this): the scatter chart + the 4 axis pickers (X/Y/Size/Colour) in the body, the
//     season select in the sub-controls strip, the population readout, and the empty states.
//     The default compare-against filter ({ league: "FAWSL" }) is applied via the engine, but
//     the chip-bar UI to EDIT it + the results table + the Chart/Table toggle are 04i-c.
//
// needsPlayer:false — a comparison isn't about one player. It builds its own .module-sub-controls
// strip (SubControls, like Rankings/Filter). Instance state (mod._cmp*) is captured by main.js
// saveState/undo/presets.
//
// The chart (Fork 2A): NOT a d3 island. renderChart builds plain innerHTML — HTML dots
// (%-positioned), SVG gridlines, an HTML legend — fully rebuilt each render (no transitions to
// protect). So the marks are plain JSX; only the magnetic hover tooltip + the pointerdown
// drag-source (over the plotted points) are wired imperatively in a useEffect.
//
// ?live=1 (Track 2 demo push): the board (`useComparisonBoard`) plots her league's Stats file
// instead of the snapshot — the canvas player's league (`_cmpSourcePlayer`), its built seasons in
// the picker, the chips the files have data for. The widget's look is unchanged.

import { ModuleExplainer, shortNameOrPlayer } from "./ModuleExplainer.js";
import { explainComparison } from "./explain.js";
import { fuzzyFilter } from "../data/fuzzy-search.js";
import { isLeagueCleared, useLeagueChip } from "../hooks/useLeagueChip.js";
import { positionRowKey, whyTwice } from "../live/group-players.js";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from "react";
import type { ProtoPlayerMetric } from "@catbento/shared";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { SubControls } from "./SubControls.js";
import { StyledSelect, type StyledSelectGroup, type StyledSelectItem } from "./StyledSelect.js";
import { FilterChipBar } from "./FilterChipBar.js";
import { type SortColumn } from "./sortable-rows.js";
import { useSortableRows } from "./useSortableRows.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import {
  formatMetricValue,
  niceDomain,
  exactDomain,
  scaleFor,
  tickValues,
  tickText,
  dotDiameterPx,
  pointColor,
  nearestPoint,
  POSITION_COLORS,
  DEFAULT_DOT_COLOR,
  type CmpColorMode,
  type CmpDomain,
} from "./comparison-view.js";
import { getPlayerShortName } from "../data/player-utils.js";
import { humanizePositionTemplate } from "./radar-view.js";
import { type ComparisonPoint } from "../data/comparison.js";
import { useComparisonMetricGroups, useComparisonMetricLookup, useComparisonBoard } from "../hooks/useComparison.js";
import {
  isRangeFilter,
  formatRangeReadout,
  formatCategoricalValue,
  FILTER_LABELS,
  type ActiveFilters,
  type RangeActive,
  type CategoricalActive,
} from "../engines/stats-filter-kit.js";
import { snapshotPlayersOrNone } from "../data/snapshot.js";
import { getStickyPlayer, setStickyPlayer } from "../canvas/sticky-player.js";
import { playerIdOf, type PlayerId } from "../data/players.js";
import { startPlayerTokenDrag, startPlayerTokenDragOnMove } from "../canvas/menu-drag.js";
import { ComparisonDetails } from "./ComparisonDetails.js";
import { hideTooltip, showTooltipAt } from "../canvas/tooltip.js";
import { LIVE } from "../live/flag.js";
import { noteRowPlayer } from "../live/player-gender.js";
import { dropLeagueChip } from "../live/comparison.js";

interface CmpChartConfig {
  xMetric: ProtoPlayerMetric;
  yMetric: ProtoPlayerMetric;
  sizeMetric: ProtoPlayerMetric | null;
  colorMetric: ProtoPlayerMetric | null;
  colorMode: CmpColorMode;
}

export function ComparisonModule({ mod, body }: ProtoModuleProps) {
  const metricGroups = useComparisonMetricGroups();
  const lookup = useComparisonMetricLookup();

  // The season picked; null = the default (the board resolves it: the snapshot's newest, or live
  // her current season).
  const [seasonPick, setSeasonPick] = useState<string | null>(() => getModState<string>(mod, "_cmpSeason") ?? null);
  const [xKey, setXKey] = useState<string>(() => getModState<string>(mod, "_cmpXKey") ?? "minutes");
  const [yKey, setYKey] = useState<string>(() => getModState<string>(mod, "_cmpYKey") ?? "passes_90");
  const [sizeKey, setSizeKey] = useState<string | null>(() => {
    const v = getModState<string | null>(mod, "_cmpSizeKey");
    return v === undefined ? null : v;
  });
  const [colorKey, setColorKey] = useState<string | null>(() => {
    const v = getModState<string | null>(mod, "_cmpColorKey");
    return v === undefined ? "pass_success_perc" : v;
  });
  const [view, setView] = useState<string>(() => (getModState<string>(mod, "_cmpView") === "table" ? "table" : "chart"));
  const [picked, setPicked] = useState<string | null>(null);

  // The module's own compare-against filter state — the same mutable object the chip-bar
  // (04i-c) will edit in place. Default { league: "FAWSL" } per direct instruction. A stable
  // reference; 04i-c adds the version-bump clone so an edit reaches the memoised points.
  const [activeFilters] = useState<ActiveFilters>(() => {
    const existing = getModState<ActiveFilters>(mod, "_cmpFilters");
    if (existing) return LIVE ? dropLeagueChip(existing) : existing;
    // A league NAME, not an id: the bio filters stay on names until Track 3 (ids-2, fork B).
    // Live: no League chip — the group is already her league (Q-010).
    const fresh: ActiveFilters = LIVE ? {} : { league: "FAWSL" };
    setModState(mod, "_cmpFilters", fresh);
    return fresh;
  });
  // Bumped by the chip-bar's onChange (an in-place edit of activeFilters) → a fresh clone per
  // version, which useComparisonPoints depends on by reference. renderers-comparison.js:589.
  const [filterVersion, setFilterVersion] = useState(0);
  const bumpFilters = useCallback(() => setFilterVersion((v) => v + 1), []);
  const activeSnapshot = useMemo<ActiveFilters>(() => ({ ...activeFilters }), [activeFilters, filterVersion]);

  // Live: the player whose league is the group. Kept (`_cmpSourcePlayer`) so an Undo or a saved
  // dashboard reopens on the same league, not whoever is sticky by then (as Filter). Snapshot:
  // unused (Ben's chips seed from the first player).
  const [sourcePlayer] = useState<PlayerId | null>(() => {
    if (!LIVE) return null;
    const existing = getModState<PlayerId>(mod, "_cmpSourcePlayer");
    if (existing) return existing;
    const sticky = getStickyPlayer();
    const first = Object.values(snapshotPlayersOrNone())[0];
    const resolved = sticky ?? (first ? playerIdOf(first) : null);
    setModState(mod, "_cmpSourcePlayer", resolved ?? undefined);
    return resolved;
  });

  const xMeta = lookup.get(xKey) ?? null;
  const yMeta = lookup.get(yKey) ?? null;
  const sizeMeta = sizeKey ? (lookup.get(sizeKey) ?? null) : null;
  const colorMeta = colorKey && colorKey !== "position" ? (lookup.get(colorKey) ?? null) : null;
  const colorMode: CmpColorMode = colorKey === "position" ? "position" : colorMeta ? "metric" : null;

  // Live: Per 90 / Total (Ben, 2026-10-16, Q-016) — Total = per 90 × 90s played, for per-90 metrics.
  const [valueMode, setValueMode] = useState<"per90" | "total">(() =>
    getModState<string>(mod, "_comparisonValueMode") === "total" ? "total" : "per90",
  );
  const hasTotal = LIVE && [xKey, yKey, sizeMeta?.metric.key, colorMeta?.metric.key].some((k) => !!k && k.endsWith("_90"));
  const mode = hasTotal ? valueMode : "per90";
  const board = useComparisonBoard({
    mode,
    leagueCleared: isLeagueCleared(mod, activeFilters, "_comparisonLeagueQuiet"),
    sourcePlayer,
    season: seasonPick,
    active: activeSnapshot,
    xKey,
    yKey,
    sizeKey: sizeMeta ? sizeMeta.metric.key : null,
    colorKey: colorMeta ? colorMeta.metric.key : null,
  });
  const { season, seasonCodes, points, chips } = board;

  // Live: "Find player…" among the players on the chart; the one picked is highlighted (Ben,
  // 2026-10-16). Kept as `_comparisonHighlight` (Undo, dashboards); gone if they leave the chart.
  const [highlight, setHighlight] = useState<PlayerId | null>(() => getModState<PlayerId>(mod, "_comparisonHighlight") ?? null);
  const [findQuery, setFindQuery] = useState("");
  const [findOpen, setFindOpen] = useState(false);
  const pickHighlight = (id: PlayerId | null, name = "") => {
    setHighlight(id);
    setModState(mod, "_comparisonHighlight", id ?? undefined);
    setFindQuery(name);
    setFindOpen(false);
  };
  const plotted = useMemo(() => {
    const seen = new Map<PlayerId, string>();
    for (const p of points) seen.set(playerIdOf(p.player), getPlayerShortName(p.player));
    return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [points]);
  const findMatches = useMemo(() => {
    const q = findQuery.trim().toLowerCase();
    // Forgiving: accents ("Odegaard" finds Ødegaard), word order, one typo; best first.
    return q ? fuzzyFilter(plotted, q, (p) => p.name).slice(0, 12) : [];
  }, [plotted, findQuery]);
  const shownHighlight = highlight !== null && plotted.some((p) => p.id === highlight) ? highlight : null;
  // Live: the League chip (useLeagueChip — quiet default = the canvas player's league; Ben's
  // prototype had `League: FAWSL` here).
  const { onChipsChange, quietKeys } = useLeagueChip(
    mod,
    activeFilters,
    board.defaultLeague,
    filterVersion,
    bumpFilters,
    "_comparisonLeagueQuiet",
  );

  // Persist the resolved selections as `_`-state (saveState/undo/presets capture them). null is
  // stored explicitly for Size/Colour so "None" survives an undo/preset (not re-defaulted).
  useEffect(() => setModState(mod, "_cmpSeason", season), [mod, season]);
  useEffect(() => setModState(mod, "_cmpXKey", xKey), [mod, xKey]);
  useEffect(() => setModState(mod, "_cmpYKey", yKey), [mod, yKey]);
  useEffect(() => setModState(mod, "_cmpSizeKey", sizeKey), [mod, sizeKey]);
  useEffect(() => setModState(mod, "_cmpColorKey", colorKey), [mod, colorKey]);
  useEffect(() => setModState(mod, "_cmpView", view), [mod, view]);
  useEffect(() => {
    body.style.justifyContent = "flex-start";
  }, [body]);


  // The 4 pickers. X/Y are grouped metric lists; Size/Colour prepend flat "None" (+ "Position"
  // for Colour) before the groups. renderers-comparison.js:664.
  const groups = metricGroups as StyledSelectGroup[];
  const sizeItems = useMemo<Array<StyledSelectItem | StyledSelectGroup>>(
    () => [{ value: "", label: "None" }, ...groups],
    [groups],
  );
  const colorItems = useMemo<Array<StyledSelectItem | StyledSelectGroup>>(
    () => [{ value: "", label: "None" }, { value: "position", label: "Position" }, ...groups],
    [groups],
  );

  // A dragged dot / row: on ?live=1 she's noted first (name + gender), so a live Stats tile can
  // take her (T2-14b). Stable, so the chart's hover/drag listeners aren't re-wired every render.
  const womens = board.womens;
  const noteDragged = useCallback(
    (p: ComparisonPoint["player"]) => {
      if (!LIVE) return;
      if (!p.name) throw new Error(`Comparison dot ${String(p.id)} has no name`);
      noteRowPlayer({ id: playerIdOf(p), name: p.name, team: p.team ?? null }, womens, p.positionTemplate);
    },
    [womens],
  );

  const seasonLabel = humanizeSeasonCode(season);

  // Live: a dot clicked → its details (Ben, 2026-10-19). By the point's key; gone when it leaves the
  // chart (a chip or season change). Clicking it again closes it.
  const pickedPoint = picked === null ? null : (points.find((p) => p.key === picked) ?? null);
  const pickDot = useCallback((p: ComparisonPoint | null) => setPicked((cur) => (p === null || cur === p.key ? null : p.key)), []);

  // No catalog at all (e.g. before the snapshot loads) — the one case the axis pickers can't
  // recover from. renderers-comparison.js:581.
  if (!xMeta || !yMeta) {
    return <div className="stats-empty">No metric catalog available.</div>;
  }

  // In Total, a per-90 metric's name says so (axis titles, legend, tooltip).
  const named = <M extends { key: string; name: string }>(m: M): M =>
    mode === "total" && m.key.endsWith("_90") ? { ...m, name: `${m.name} (total)` } : m;
  const cfg: CmpChartConfig = {
    xMetric: named(xMeta.metric),
    yMetric: named(yMeta.metric),
    sizeMetric: sizeMeta ? named(sizeMeta.metric) : null,
    colorMetric: colorMeta ? named(colorMeta.metric) : null,
    colorMode,
  };
  // The details panel's stats: X and Y, then size and colour when set (a metric).
  const detailStats = [
    { role: "x", key: xMeta.metric.key, name: cfg.xMetric.name, outputType: cfg.xMetric.outputType },
    { role: "y", key: yMeta.metric.key, name: cfg.yMetric.name, outputType: cfg.yMetric.outputType },
    ...(cfg.sizeMetric && sizeMeta ? [{ role: "size", key: sizeMeta.metric.key, name: cfg.sizeMetric.name, outputType: cfg.sizeMetric.outputType }] : []),
    ...(cfg.colorMode === "metric" && cfg.colorMetric && colorMeta
      ? [{ role: "color", key: colorMeta.metric.key, name: cfg.colorMetric.name, outputType: cfg.colorMetric.outputType }]
      : []),
  ];

  // Empty-state message: filtered-to-zero names the active filters, otherwise it's an honest
  // data gap for these two axes this season. renderers-comparison.js:705.
  // Live with no group: why (e.g. "Premier League 22/23 isn't built yet"), never a guess.
  const activeKeys = Object.keys(activeSnapshot).filter((k) => activeSnapshot[k] !== undefined);
  const ranges = chips.rangeFilters;
  const emptyMessage = board.noGroup
    ? `${board.noGroup}.`
    : activeKeys.length
      ? `No players match all of: ${activeKeys
          .map((k) =>
            isRangeFilter(k, ranges)
              ? `${ranges[k]!.chipLabel}: ${formatRangeReadout(k, activeSnapshot[k] as RangeActive, ranges)}`
              : `${FILTER_LABELS[k]}: ${formatCategoricalValue(activeSnapshot[k] as CategoricalActive)}`,
          )
          .join(", ")}. Remove a filter to widen the comparison.`
      : `No real players have both ${xMeta.metric.name} and ${yMeta.metric.name} recorded in ${seasonLabel}.`;

  return (
    <>
      {LIVE && (
        <ModuleExplainer
          mod={mod}
          paragraphs={explainComparison({
            x: cfg.xMetric.name,
            y: cfg.yMetric.name,
            size: cfg.sizeMetric?.name ?? null,
            colour: colorMode === "position" ? "position" : (cfg.colorMetric?.name ?? null),
            season: seasonLabel,
            mode,
            filters: activeSnapshot,
            refName: shortNameOrPlayer(sourcePlayer),
            count: board.count,
            noGroup: board.noGroup ?? null,
            table: view === "table",
            highlight: plotted.find((p) => p.id === shownHighlight)?.name ?? null,
          })}
        />
      )}
      <SubControls mod={mod} body={body}>
        <StyledSelect
          items={seasonCodes.map((c) => ({ value: c, label: humanizeSeasonCode(c) }))}
          value={season}
          onChange={(next) => {
            setSeasonPick(next);
            setModState(mod, "_cmpSeason", next);
          }}
          wrapClass="cmp-season-select-wrap"
          extraClass="cmp-season-select"
        />
        {/* The toggle names the view a click opens (Ben, 2026-10-31); the prototype named the active one. */}
        <button
          type="button"
          className="module-switch-btn cmp-view-toggle"
          onClick={() => setView((v) => (v === "table" ? "chart" : "table"))}
        >
          {view === "table" ? "Chart" : "Table"}
        </button>
      </SubControls>

      <div className="cmp-wrap">
        <div className="cmp-axis-controls">
          {/* Each picker's caption sits in a <div>, not a <label>: a label passes a second click to
              the picker button inside it, so a picked option's menu opened again. Ben's prototype
              redrew the whole widget after a pick, which hid it; React updates in place. */}
          <div className="cmp-axis-control">
            <span>X axis</span>
            <StyledSelect items={groups} value={xKey} onChange={setXKey} wrapClass="cmp-x-select-wrap" extraClass="cmp-metric-select" searchable />
          </div>
          <div className="cmp-axis-control">
            <span>Y axis</span>
            <StyledSelect items={groups} value={yKey} onChange={setYKey} wrapClass="cmp-y-select-wrap" extraClass="cmp-metric-select" searchable />
          </div>
          <div className="cmp-axis-control">
            <span>Bubble size</span>
            <StyledSelect
              items={sizeItems}
              value={sizeKey ?? ""}
              onChange={(v) => setSizeKey(v || null)}
              wrapClass="cmp-size-select-wrap"
              extraClass="cmp-metric-select"
              searchable
            />
          </div>
          <div className="cmp-axis-control">
            <span>Colour</span>
            <StyledSelect
              items={colorItems}
              value={colorKey ?? ""}
              onChange={(v) => setColorKey(v || null)}
              wrapClass="cmp-color-select-wrap"
              extraClass="cmp-metric-select"
              searchable
            />
          </div>
        </div>

        {/* .filter-bar: the shared React FilterChipBar (chips + "+ Add filter"), absolute ranges.
            Live with no group (a league's season isn't built yet): the message says why, and the
            chips stay so another league can be picked (Ben, 2026-10-16). */}
        {(!board.noGroup || LIVE) && (
          <div className="filter-bar">
            {LIVE && (
              <div className="ranking-player-search-wrap cmp-find-wrap">
                <input
                  type="text"
                  className="ranking-player-search"
                  placeholder="Find player…"
                  autoComplete="off"
                  spellCheck={false}
                  value={findQuery}
                  onChange={(e) => {
                    setFindQuery(e.target.value);
                    setFindOpen(true);
                  }}
                  onFocus={() => setFindOpen(true)}
                  onBlur={() => setFindOpen(false)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && findMatches[0]) pickHighlight(findMatches[0].id, findMatches[0].name);
                    else if (e.key === "Escape") setFindOpen(false);
                  }}
                />
                {shownHighlight !== null && (
                  <button type="button" className="ranking-search-clear" title="Clear" onClick={() => pickHighlight(null)}>
                    ×
                  </button>
                )}
                {findOpen && findQuery.trim() !== "" && (
                  <div className="ranking-player-search-results open">
                    {findMatches.length ? (
                      findMatches.map((m) => (
                        <div
                          key={m.id}
                          className="ranking-player-search-option"
                          // Keep focus while clicking, so the pick lands (the list closes on blur).
                          onMouseDown={(ev) => ev.preventDefault()}
                          onClick={() => pickHighlight(m.id, m.name)}
                        >
                          <span>{m.name}</span>
                        </div>
                      ))
                    ) : (
                      <div className="ranking-player-search-empty">Not on this chart</div>
                    )}
                  </div>
                )}
              </div>
            )}
            <FilterChipBar
              active={activeFilters}
              refPlayer={chips.refPlayer}
              options={chips.options}
              rangeFilters={chips.rangeFilters}
              addMenuKeys={chips.addMenuKeys}
              newChipValue={chips.newChipValue}
              onChange={onChipsChange}
              quietKeys={quietKeys}
              optionInfo={chips.optionInfo}
            />
          </div>
        )}

        <div className={`population-readout${points.length < 1 ? " zero" : ""}`}>
          <span className="population-count">{board.count.toLocaleString()}</span>
          <span className="population-label">{board.label}</span>
          {hasTotal && (
            <button
              type="button"
              className="value-mode-toggle"
              data-mode={mode}
              onClick={() => {
                const next = mode === "total" ? "per90" : "total";
                setModState(mod, "_comparisonValueMode", next);
                setValueMode(next);
              }}
            >
              {mode === "total" ? "Total" : "Per 90"}
            </button>
          )}
        </div>

        {points.length < 1 ? (
          <div className="stats-empty">{emptyMessage}</div>
        ) : (
          <div className="cmp-area">
            {view === "table" ? (
              <ComparisonTable points={points} cfg={cfg} onDrag={noteDragged} highlight={shownHighlight} />
            ) : (
              <div className="cmp-chart-and-details">
                <ComparisonChart
                  points={points}
                  cfg={cfg}
                  onDrag={noteDragged}
                  highlight={shownHighlight}
                  picked={pickedPoint?.key ?? null}
                  onPick={pickDot}
                />
                {LIVE && pickedPoint && (
                  <ComparisonDetails
                    point={pickedPoint}
                    points={points}
                    stats={detailStats}
                    onClose={() => setPicked(null)}
                    onDrag={noteDragged}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// --- the scatter chart (JSX marks + a thin imperative hover/drag effect) ------------------

interface RenderDot {
  leftPct: number;
  topPct: number;
  diameter: number;
  color: string;
  player: ComparisonPoint["player"];
  raw: ComparisonPoint;
}


/** Live: the "why twice" hover (Q-014) for players shown once per position — from the dots / rows
 *  actually shown, so it only names positions that are ticked. Empty on the snapshot site. */
function whyTwiceOf(players: readonly { playerId?: number; competition?: string | null; stats?: Record<string, number | null | undefined>; name?: string }[]): Map<string, string> {
  return whyTwice(
    players.map((p) => ({
      playerId: p.playerId ?? -1,
      shortName: getPlayerShortName(p),
      competition: p.competition ?? null,
      positionMinutes: p.stats?.minutes ?? null,
    })),
  );
}

function comparisonTooltip(point: ComparisonPoint, cfg: CmpChartConfig, twice?: Map<string, string>): string {
  const pl = point.player;
  const meta = [pl.team, pl.competition].filter(Boolean).join(" · ");
  const lines = [
    getPlayerShortName(pl),
    meta,
    `${cfg.xMetric.name}: ${formatMetricValue(point.x, cfg.xMetric.outputType)}`,
    `${cfg.yMetric.name}: ${formatMetricValue(point.y, cfg.yMetric.outputType)}`,
  ];
  if (cfg.sizeMetric && point.size != null) lines.push(`${cfg.sizeMetric.name}: ${formatMetricValue(point.size, cfg.sizeMetric.outputType)}`);
  if (cfg.colorMode === "metric" && cfg.colorMetric && point.color != null)
    lines.push(`${cfg.colorMetric.name}: ${formatMetricValue(point.color, cfg.colorMetric.outputType)}`);
  if (cfg.colorMode === "position") lines.push(humanizePositionTemplate(pl.positionTemplate ?? ""));
  const why = twice?.get(positionRowKey({ playerId: (pl as { playerId?: number }).playerId ?? -1, competition: pl.competition ?? null }));
  if (why) lines.push(why);
  return lines.join("\n");
}

type OnDrag = (player: ComparisonPoint["player"]) => void;

function ComparisonChart({
  points,
  cfg,
  onDrag,
  highlight = null,
  picked = null,
  onPick,
}: {
  points: ComparisonPoint[];
  cfg: CmpChartConfig;
  onDrag: OnDrag;
  /** Live: the dot whose details are open (ringed). */
  picked?: string | null;
  /** Live: a click (no drag) on a dot, or on the empty chart (null). */
  onPick?: (point: ComparisonPoint | null) => void;
  /** Live: the found player — her bubble(s) ringed, on top and named; the rest dimmed. */
  highlight?: PlayerId | null;
}) {
  const plotBoxRef = useRef<HTMLDivElement>(null);
  const twice = useMemo(() => whyTwiceOf(points.map((p) => p.player)), [points]);

  const derived = useMemo(() => {
    const xDomain = niceDomain(points.map((p) => p.x));
    const yDomain = niceDomain(points.map((p) => p.y));
    const sizeVals = cfg.sizeMetric ? points.map((p) => p.size).filter((v): v is number => v != null) : [];
    const sizeDomain: CmpDomain | null = sizeVals.length ? niceDomain(sizeVals) : null;
    const colorVals = cfg.colorMode === "metric" ? points.map((p) => p.color).filter((v): v is number => v != null) : [];
    // Live: the colour key ends at the real highest value (Q-016); the demo keeps its padding.
    const colorDomain: CmpDomain | null = colorVals.length ? (LIVE ? exactDomain(colorVals) : niceDomain(colorVals)) : null;

    // Plain 0–100% of the plot box (the fixed-pixel margin lives in .cmp-plot-box's CSS, so the
    // mapping carves out no margin of its own). y inverted (0% = top). renderers-comparison.js:323.
    const sx = scaleFor(xDomain, 0, 100);
    const sy = scaleFor(yDomain, 100, 0);
    const xTicks = tickValues(xDomain, 5);
    const yTicks = tickValues(yDomain, 5);

    // Extreme-value bubbles paint on top of the boring middle (biggest/most-saturated last), like
    // the Shot/Pass maps' colour-by paint order. renderers-comparison.js:337.
    const ordered = points.slice().sort((a, b) => {
      const av = (cfg.sizeMetric ? a.size : null) ?? (cfg.colorMode === "metric" ? a.color : null) ?? 0;
      const bv = (cfg.sizeMetric ? b.size : null) ?? (cfg.colorMode === "metric" ? b.color : null) ?? 0;
      // The found player's bubbles last, so they're drawn on top.
      const ah = highlight !== null && playerIdOf(a.player) === highlight ? 1 : 0;
      const bh = highlight !== null && playerIdOf(b.player) === highlight ? 1 : 0;
      return ah - bh || Math.abs(av) - Math.abs(bv);
    });
    const dots: RenderDot[] = ordered.map((p) => ({
      leftPct: sx(p.x),
      topPct: sy(p.y),
      diameter: dotDiameterPx(sizeDomain, p.size),
      color: pointColor(cfg.colorMode, colorDomain, p.player, p.color),
      player: p.player,
      raw: p,
    }));
    return { sx, sy, xTicks, yTicks, sizeDomain, colorDomain, dots };
  }, [points, cfg, highlight]);

  // Magnetic hover + drag-source, wired imperatively over the plotted dots (the JSX marks are
  // static; only this pointer math is imperative). Re-runs when the derived dots / cfg change.
  useEffect(() => {
    const el = plotBoxRef.current;
    if (!el) return;
    // Listen on the plot's wrapper, not just the plot box: a bubble at the edge of the
    // plot overhangs it, and hovering / grabbing that overhang should work too. The
    // hit maths still uses the plot box's own rect.
    const host = el.parentElement ?? el;
    const dots = derived.dots;
    const found = (d: RenderDot) => highlight !== null && playerIdOf(d.player) === highlight;
    const onMove = (e: MouseEvent) => {
      const hit = nearestPoint(el.getBoundingClientRect(), dots, e.clientX, e.clientY, found);
      // A class, not el.style.cursor: the pointer is really over the chart's SVG/inner
      // layers, which styles.css's `.module-body *` pins to the plain arrow — so the
      // grab has to be applied to them too (see .cmp-chart-inner.cmp-dot-hover).
      host.classList.toggle("cmp-dot-hover", !!hit);
      if (hit) showTooltipAt(comparisonTooltip(hit.raw, cfg, twice), e.clientX, e.clientY);
      else hideTooltip();
    };
    const onLeave = () => {
      host.classList.remove("cmp-dot-hover");
      hideTooltip();
    };
    // Every dot is a drag source (→ Bio on empty canvas, or joins a Radar/Stats), via
    // startPlayerTokenDrag (drag-to-add). A plain click never triggers it (it requires real pointer movement),
    // so hovering to browse stays safe. Desktop only, like every startPlayerTokenDrag call site.
    const onDown = (e: PointerEvent) => {
      if (window.innerWidth <= 768) return;
      const hit = nearestPoint(el.getBoundingClientRect(), dots, e.clientX, e.clientY, found);
      // Live (Ben, 2026-10-19): a click opens the dot's details; the drag starts only once the pointer
      // moves (as Similarity's rows). A click on the empty chart closes the details.
      if (LIVE && onPick) {
        if (!hit) {
          onPick(null);
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        let dragged = false;
        startPlayerTokenDragOnMove(e, () => {
          dragged = true;
          onDrag(hit.player);
          setStickyPlayer(playerIdOf(hit.player));
        });
        window.addEventListener("pointerup", () => !dragged && onPick(hit.raw), { once: true });
        return;
      }
      if (hit) {
        e.preventDefault();
        e.stopPropagation();
        onDrag(hit.player);
        setStickyPlayer(playerIdOf(hit.player));
        startPlayerTokenDrag(e);
      }
    };
    host.addEventListener("mousemove", onMove);
    host.addEventListener("mouseleave", onLeave);
    host.addEventListener("pointerdown", onDown);
    return () => {
      host.classList.remove("cmp-dot-hover");
      host.removeEventListener("mousemove", onMove);
      host.removeEventListener("mouseleave", onLeave);
      host.removeEventListener("pointerdown", onDown);
      hideTooltip();
    };
  }, [derived, cfg, onDrag, onPick, highlight]);

  const { sx, sy, xTicks, yTicks, sizeDomain, colorDomain, dots } = derived;

  return (
    <>
      <div className="cmp-chart-inner">
        <div className="cmp-plot-box" ref={plotBoxRef}>
          <svg className="cmp-chart-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
            <line className="cmp-axis-line" x1="0" y1="0" x2="0" y2="100" />
            <line className="cmp-axis-line" x1="0" y1="100" x2="100" y2="100" />
            <g className="cmp-gridlines">
              {xTicks.map((t, i) => (
                <line key={`x${i}`} className="cmp-grid-line" x1={sx(t)} y1={0} x2={sx(t)} y2={100} />
              ))}
              {yTicks.map((t, i) => (
                <line key={`y${i}`} className="cmp-grid-line" x1={0} y1={sy(t)} x2={100} y2={sy(t)} />
              ))}
            </g>
          </svg>
          <div className={`cmp-dots-overlay${highlight !== null ? " has-highlight" : ""}`}>
            {dots.map((d, i) => {
              const found = highlight !== null && playerIdOf(d.player) === highlight;
              return (
                <div
                  key={`${d.player.name}-${i}`}
                  className={`cmp-dot${found ? " cmp-dot-highlight" : ""}${picked !== null && d.raw.key === picked ? " cmp-dot-picked" : ""}`}
                  style={{
                    left: `${d.leftPct.toFixed(2)}%`,
                    top: `${d.topPct.toFixed(2)}%`,
                    width: `${d.diameter.toFixed(1)}px`,
                    height: `${d.diameter.toFixed(1)}px`,
                    background: d.color,
                  }}
                >
                  {/* The found player's tooltip, pinned on (Ben, 2026-10-16): her info shows without
                      hovering; it opens to the left when her bubble is on the right of the chart. */}
                  {found && (
                    <span className={`cmp-dot-pinned${d.leftPct > 60 ? " cmp-dot-pinned-left" : ""}`}>
                      {comparisonTooltip(d.raw, cfg, twice)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="cmp-labels-overlay">
            {xTicks.map((t, i) => (
              <span key={`tx${i}`} className="cmp-tick cmp-tick-x" style={{ left: `${sx(t)}%` }}>
                {tickText(t, cfg.xMetric.outputType)}
              </span>
            ))}
            {yTicks.map((t, i) => (
              <span key={`ty${i}`} className="cmp-tick cmp-tick-y" style={{ top: `${sy(t)}%` }}>
                {tickText(t, cfg.yMetric.outputType)}
              </span>
            ))}
            <span className="cmp-axis-title cmp-axis-title-x" style={{ left: "50%" }}>
              {cfg.xMetric.name}
            </span>
            <span className="cmp-axis-title cmp-axis-title-y" style={{ top: "50%" }}>
              {cfg.yMetric.name}
            </span>
          </div>
        </div>
      </div>
      <ComparisonLegend points={points} cfg={cfg} sizeDomain={sizeDomain} colorDomain={colorDomain} />
    </>
  );
}

// --- the results table (JSX + useSortableRows + drag rows) --------------------------------

function ComparisonTable({
  points,
  cfg,
  onDrag,
  highlight = null,
}: {
  points: ComparisonPoint[];
  cfg: CmpChartConfig;
  onDrag: OnDrag;
  /** Live: the found player's row(s), highlighted. */
  highlight?: PlayerId | null;
}) {
  // Default order: x DESC (renderers-comparison.js:492). Memoised on `points` so it's stable
  // across unrelated re-renders — useSortableRows resets the sort when its source array changes.
  const defaultSorted = useMemo(() => points.slice().sort((a, b) => b.x - a.x), [points]);

  // Columns in header order; Player/Team/Position are the legacy's non-sortable .no-sort cells,
  // the metric columns sort numerically. Identity kept stable per metric set.
  const columns = useMemo<SortColumn<ComparisonPoint>[]>(() => {
    const cols: SortColumn<ComparisonPoint>[] = [
      { label: "Player", get: (p) => getPlayerShortName(p.player), sortable: false },
      { label: "Team", get: (p) => [p.player.team, p.player.competition].filter(Boolean).join(" · "), sortable: false },
      { label: "Position", get: (p) => humanizePositionTemplate(p.player.positionTemplate ?? ""), sortable: false },
      { label: cfg.xMetric.name, get: (p) => p.x },
      { label: cfg.yMetric.name, get: (p) => p.y },
    ];
    if (cfg.sizeMetric) cols.push({ label: cfg.sizeMetric.name, get: (p) => p.size });
    if (cfg.colorMode === "metric" && cfg.colorMetric) cols.push({ label: cfg.colorMetric.name, get: (p) => p.color });
    return cols;
  }, [cfg]);

  const { rows, headerProps, rankProps, containerRef } = useSortableRows(defaultSorted, columns);
  const twice = useMemo(() => whyTwiceOf(points.map((p) => p.player)), [points]);

  // The metric columns beyond X/Y (size / colour), for the extra header + cells.
  const extraCols: { label: string; get: (p: ComparisonPoint) => number | null; type: string }[] = [];
  if (cfg.sizeMetric) extraCols.push({ label: cfg.sizeMetric.name, get: (p) => p.size, type: cfg.sizeMetric.outputType });
  if (cfg.colorMode === "metric" && cfg.colorMetric)
    extraCols.push({ label: cfg.colorMetric.name, get: (p) => p.color, type: cfg.colorMetric.outputType });

  // Every row is a drag source (→ Bio on empty canvas, or joins a Radar/Stats), via
  // startPlayerTokenDrag — same as the chart's dots + Squad/Rankings rows. Desktop only.
  const onRowPointerDown = (e: ReactPointerEvent<HTMLDivElement>, player: ComparisonPoint["player"]) => {
    if (window.innerWidth <= 768) return;
    e.preventDefault();
    e.stopPropagation();
    onDrag(player);
    setStickyPlayer(playerIdOf(player));
    startPlayerTokenDrag(e.nativeEvent);
  };

  return (
    <div className="data-wrapper top-align cmp-table" ref={containerRef}>
      <div className="data-row header">
        <div className="col-rank" onClick={rankProps.onClick}>
          #
        </div>
        <div className="col-team no-sort">Player</div>
        <div className="col-team no-sort">Team</div>
        <div className="col-team no-sort">Position</div>
        <div className={`col-num ${headerProps(3).className}`.trim()} onClick={headerProps(3).onClick}>
          {cfg.xMetric.name}
        </div>
        <div className={`col-num ${headerProps(4).className}`.trim()} onClick={headerProps(4).onClick}>
          {cfg.yMetric.name}
        </div>
        {extraCols.map((c, i) => (
          <div key={c.label} className={`col-num ${headerProps(5 + i).className}`.trim()} onClick={headerProps(5 + i).onClick}>
            {c.label}
          </div>
        ))}
      </div>
      {rows.map((p, i) => (
        <div
          key={p.key}
          data-sort-key={p.key}
          className={`data-row cmp-table-row${highlight !== null && playerIdOf(p.player) === highlight ? " ranking-row-jumped" : ""}`}
          onPointerDown={(e) => onRowPointerDown(e, p.player)}
          data-tooltip={twice.get(
            positionRowKey({ playerId: (p.player as { playerId?: number }).playerId ?? -1, competition: p.player.competition ?? null }),
          )}
        >
          <div className="col-rank">{i + 1}</div>
          <div className="col-team">{getPlayerShortName(p.player)}</div>
          <div className="col-team">{[p.player.team, p.player.competition].filter(Boolean).join(" · ")}</div>
          <div className="col-team">{humanizePositionTemplate(p.player.positionTemplate ?? "")}</div>
          <div className="col-num" data-val={p.x}>
            {formatMetricValue(p.x, cfg.xMetric.outputType)}
          </div>
          <div className="col-num" data-val={p.y}>
            {formatMetricValue(p.y, cfg.yMetric.outputType)}
          </div>
          {extraCols.map((c) => {
            const v = c.get(p);
            return (
              <div key={c.label} className="col-num" data-val={v == null ? "" : v}>
                {v == null ? "—" : formatMetricValue(v, c.type)}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function ComparisonLegend({
  points,
  cfg,
  sizeDomain,
  colorDomain,
}: {
  points: ComparisonPoint[];
  cfg: CmpChartConfig;
  sizeDomain: CmpDomain | null;
  colorDomain: CmpDomain | null;
}) {
  const parts: ReactNode[] = [];
  if (cfg.colorMode === "position") {
    const present = Array.from(new Set(points.map((p) => p.player.positionTemplate ?? ""))).sort();
    parts.push(
      <div className="cmp-legend-group" key="pos">
        {present.map((tpl) => (
          <span className="cmp-legend-chip" key={tpl}>
            <span className="cmp-legend-dot" style={{ background: POSITION_COLORS[tpl] || DEFAULT_DOT_COLOR }} />
            {humanizePositionTemplate(tpl)}
          </span>
        ))}
      </div>,
    );
  } else if (cfg.colorMode === "metric" && colorDomain && cfg.colorMetric) {
    parts.push(
      <div className="cmp-legend-group cmp-legend-gradient" key="grad">
        <span>{tickText(colorDomain.min, cfg.colorMetric.outputType)}</span>
        <div className="cmp-legend-gradient-bar" />
        <span>{tickText(colorDomain.max, cfg.colorMetric.outputType)}</span>
        <span className="cmp-legend-label">{cfg.colorMetric.name}</span>
      </div>,
    );
  }
  if (sizeDomain && cfg.sizeMetric) {
    const sizeMetric = cfg.sizeMetric; // capture for the .map closure (narrowing is otherwise lost)
    const sample = [sizeDomain.min, (sizeDomain.min + sizeDomain.max) / 2, sizeDomain.max];
    parts.push(
      <div className="cmp-legend-group cmp-legend-size" key="size">
        <span className="cmp-legend-label">{sizeMetric.name}</span>
        {sample.map((v, i) => (
          <span className="cmp-legend-bubble-wrap" key={i}>
            <span
              className="cmp-legend-bubble"
              style={{ width: `${dotDiameterPx(sizeDomain, v).toFixed(1)}px`, height: `${dotDiameterPx(sizeDomain, v).toFixed(1)}px` }}
            />
            <span className="cmp-legend-bubble-value">{tickText(v, sizeMetric.outputType)}</span>
          </span>
        ))}
      </div>,
    );
  }
  return (
    <div className="cmp-legend" style={parts.length ? undefined : { display: "none" }}>
      {parts}
    </div>
  );
}
